import React, { useState, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp, getDocs, query, orderBy, limit, doc, updateDoc } from 'firebase/firestore';
import { storage, db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import PublicNavigation from '../../components/PublicNavigation';
import ReportPreview from './ReportPreview';
import './ReportUpload.css';

function ReportUpload() {
  const { currentUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedReports, setUploadedReports] = useState([]);
  const [currentFile, setCurrentFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [viewingReport, setViewingReport] = useState(null);

  useEffect(() => {
    loadRecentReports();
  }, []);

  async function loadRecentReports() {
    try {
      const q = query(
        collection(db, 'monthlyReports'),
        orderBy('uploadedAt', 'desc'),
        limit(10)
      );
      const snapshot = await getDocs(q);
      const reports = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        uploadedAt: doc.data().uploadedAt?.toDate()
      }));
      setUploadedReports(reports);
    } catch (error) {
      console.error('Error loading reports:', error);
    }
  }

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    // Validate file type
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!validTypes.includes(file.type)) {
      alert('Please upload a PDF, DOCX, or XLSX file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setCurrentFile(file);
  };

  const uploadReport = async () => {
    if (!currentFile) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      // Create unique filename
      const timestamp = Date.now();
      const fileName = `${timestamp}_${currentFile.name}`;
      const storageRef = ref(storage, `reports/${fileName}`);

      // Upload file
      const snapshot = await uploadBytes(storageRef, currentFile);
      setUploadProgress(50);

      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      setUploadProgress(75);

      // Extract file type
      const fileType = currentFile.name.endsWith('.pdf') ? 'pdf' : 
                       currentFile.name.endsWith('.docx') ? 'docx' : 'xlsx';

      // Save to Firestore
      const reportDoc = await addDoc(collection(db, 'monthlyReports'), {
        fileName: currentFile.name,
        fileUrl: downloadURL,
        fileType: fileType,
        fileSize: currentFile.size,
        uploadedBy: currentUser.uid,
        uploadedByEmail: currentUser.email,
        uploadedAt: serverTimestamp(),
        status: 'pending',
        extractedData: null,
        mapping: null
      });

      setUploadProgress(100);

      // Add to uploaded reports list
      setUploadedReports([
        {
          id: reportDoc.id,
          fileName: currentFile.name,
          fileType: fileType,
          uploadedAt: new Date(),
          status: 'pending'
        },
        ...uploadedReports
      ]);

      // Reset
      setTimeout(() => {
        setCurrentFile(null);
        setUploadProgress(0);
        setUploading(false);
        alert('Report uploaded successfully! Processing will begin shortly.');
      }, 1000);

    } catch (error) {
      console.error('Error uploading report:', error);
      alert('Failed to upload report: ' + error.message);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType === 'pdf') return '📄';
    if (fileType === 'docx') return '📝';
    if (fileType === 'xlsx') return '📊';
    return '📁';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="report-upload-container">
      <PublicNavigation />
      
      <div className="report-upload-content">
        <div className="upload-header">
          <h1>Upload Monthly Report</h1>
          <p className="upload-subtitle">
            Upload monthly activity reports to automatically extract and process project data
          </p>
        </div>

        {/* Upload Area */}
        <div className="upload-section">
          <div 
            className={`upload-dropzone ${dragActive ? 'drag-active' : ''} ${currentFile ? 'has-file' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {!currentFile ? (
              <>
                <div className="upload-icon">📤</div>
                <h3>Drag and drop your report here</h3>
                <p>or</p>
                <label htmlFor="file-upload" className="upload-button">
                  Browse Files
                </label>
                <input
                  id="file-upload"
                  type="file"
                  accept=".pdf,.docx,.xlsx"
                  onChange={handleChange}
                  style={{ display: 'none' }}
                />
                <p className="upload-hint">
                  Supported formats: PDF, DOCX, XLSX (Max 10MB)
                </p>
              </>
            ) : (
              <div className="file-preview">
                <div className="file-info">
                  <span className="file-icon-large">
                    {getFileIcon(currentFile.name.split('.').pop())}
                  </span>
                  <div className="file-details">
                    <h4>{currentFile.name}</h4>
                    <p>{formatFileSize(currentFile.size)}</p>
                  </div>
                </div>
                
                {uploadProgress > 0 && (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <span className="progress-text">{uploadProgress}%</span>
                  </div>
                )}

                <div className="file-actions">
                  <button 
                    onClick={uploadReport} 
                    disabled={uploading}
                    className="upload-confirm-button"
                  >
                    {uploading ? 'Uploading...' : '✓ Upload Report'}
                  </button>
                  <button 
                    onClick={() => setCurrentFile(null)} 
                    disabled={uploading}
                    className="upload-cancel-button"
                  >
                    ✕ Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="upload-instructions">
            <h3>📋 Upload Instructions</h3>
            <ol>
              <li>Prepare your monthly activity report in PDF, DOCX, or Excel format</li>
              <li>Ensure the report includes:
                <ul>
                  <li>Financial summary (opening balance, received, disbursed, closing)</li>
                  <li>Project updates with beneficiary counts</li>
                  <li>Chapter/donor information</li>
                  <li>Any new projects approved</li>
                </ul>
              </li>
              <li>Upload the file using drag-and-drop or browse button</li>
              <li>The system will automatically:
                <ul>
                  <li>Extract data from tables and text</li>
                  <li>Match projects to existing database entries</li>
                  <li>Present mapping for your review</li>
                </ul>
              </li>
              <li>Review and approve the extracted data before applying to projects</li>
            </ol>
          </div>
        </div>

        {/* Recent Uploads */}
        {uploadedReports.length > 0 && (
          <div className="recent-uploads-section">
            <h3>Recent Uploads</h3>
            <div className="uploads-list">
              {uploadedReports.map(report => (
                <div key={report.id} className="upload-item">
                  <span className="file-icon">{getFileIcon(report.fileType)}</span>
                  <div className="upload-item-details">
                    <h4>{report.fileName}</h4>
                    <p>{report.uploadedAt.toLocaleString()}</p>
                  </div>
                  <span className={`status-badge status-${report.status}`}>
                    {report.status}
                  </span>
                  <button 
                    className="view-report-button"
                    onClick={() => setViewingReport(report)}
                  >
                    View →
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sample Template */}
        <div className="template-section">
          <h3>📥 Need a Template?</h3>
          <p>Download our sample monthly report template to ensure proper formatting:</p>
          <div className="template-buttons">
            <button className="template-download-button">
              📄 Download PDF Template
            </button>
            <button className="template-download-button">
              📝 Download DOCX Template
            </button>
            <button className="template-download-button">
              📊 Download Excel Template
            </button>
          </div>
        </div>
      </div>

      {/* Report Preview Modal */}
      {viewingReport && (
        <ReportPreview
          reportId={viewingReport.id}
          onClose={() => setViewingReport(null)}
          onApprove={async (report) => {
            try {
              // For now, just mark as approved
              // Full mapping workflow will be added later
              const reportRef = doc(db, 'monthlyReports', report.id);
              await updateDoc(reportRef, {
                status: 'approved',
                approvedAt: serverTimestamp()
              });
              
              alert(`✅ Report approved!\n\n${report.extractedData?.projectBlocks?.length || 0} projects extracted and ready for mapping.`);
              setViewingReport(null);
              loadRecentReports();
            } catch (error) {
              console.error('Error approving report:', error);
              alert('Failed to approve report');
            }
          }}
        />
      )}
    </div>
  );
}

export default ReportUpload;
